import type { MetricIdentity } from "@/metrics/catalog"
import {
  CompareByColorSection,
  type DistinctCompareByValue,
} from "@/run-detail/compare-by"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from "@coval/ui"
import { Eye, EyeOff, SlidersHorizontal } from "lucide-react"

import type { MetricViews } from "@/types/MetricViewConfig"

import {
  isTriageCategoryVisible,
  type ConversationTriageCategory,
  type ConversationTriageVisibility,
} from "../utils/reportTriage"
import { MetricViewsSection } from "./MetricViewsSection"

interface ReportSettingsMenuProps {
  metricIds: string[]
  metricRecordsById: Record<string, MetricIdentity>
  metricViews: MetricViews
  onMetricViewsChange: (metricViews: MetricViews) => void
  distinctCompareByValues: DistinctCompareByValue[]
  compareByValueToColor: Record<string, string>
  setCompareByColor: (overrideKey: string, color: string) => void
  /**
   * Conversation Triage section only renders for reports linked to an HR
   * project. Omit these on surfaces with no HR context (e.g. the new-report page).
   */
  isHumanReviewLinked?: boolean
  triageCategories?: ConversationTriageCategory[]
  triageVisibility?: ConversationTriageVisibility
  onToggleTriageCategory?: (label: string, visible: boolean) => void
  isTriageLoading?: boolean
  humanReviewSimsError?: boolean
}

/** Report header gear menu grouping metric visibility, conversation triage, and compare-by colors. */
export function ReportSettingsMenu({
  metricIds,
  metricRecordsById,
  metricViews,
  onMetricViewsChange,
  distinctCompareByValues,
  compareByValueToColor,
  setCompareByColor,
  isHumanReviewLinked = false,
  triageCategories = [],
  triageVisibility = {},
  onToggleTriageCategory = () => {},
  isTriageLoading = false,
  humanReviewSimsError = false,
}: ReportSettingsMenuProps) {
  const showMetrics = metricIds.length > 0
  const showColors = distinctCompareByValues.length > 0
  const showTriage = isHumanReviewLinked

  const defaultOpen = [
    showMetrics ? "metrics" : null,
    showTriage ? "triage" : null,
    showColors ? "colors" : null,
  ].filter((value): value is string => value !== null)

  if (defaultOpen.length === 0) {
    return null
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="secondary" icon tooltip="Report settings">
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="w-72 p-0">
        <Accordion type="multiple" defaultValue={defaultOpen}>
          {showMetrics && (
            <AccordionItem value="metrics">
              <AccordionTrigger className="px-3">Metrics</AccordionTrigger>
              <AccordionContent className="pb-1">
                <MetricViewsSection
                  metricIds={metricIds}
                  metricRecordsById={metricRecordsById}
                  metricViews={metricViews}
                  onMetricViewsChange={onMetricViewsChange}
                />
              </AccordionContent>
            </AccordionItem>
          )}

          {showTriage && (
            <AccordionItem value="triage">
              <AccordionTrigger className="px-3">
                Conversation triage
              </AccordionTrigger>
              <AccordionContent className="pb-1">
                {isTriageLoading ? (
                  <span className="block px-3 pb-2 text-xs text-coval-text-muted/60">
                    Loading…
                  </span>
                ) : humanReviewSimsError ? (
                  <span className="block px-3 pb-2 text-xs text-coval-danger-text">
                    Human review labels failed to load
                  </span>
                ) : triageCategories.length === 0 ? (
                  <span className="block px-3 pb-2 text-xs text-coval-text-muted/60">
                    No conversation triage labels
                  </span>
                ) : (
                  triageCategories.map((category) => {
                    const isVisible = isTriageCategoryVisible(
                      triageVisibility,
                      category.label,
                    )
                    return (
                      <Tooltip
                        key={category.label}
                        content={category.label}
                        side="left"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            onToggleTriageCategory(category.label, !isVisible)
                          }
                          className={`flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-surface-1 cursor-pointer ${
                            isVisible
                              ? "text-coval-text-muted"
                              : "text-coval-text-muted/40"
                          }`}
                          aria-label={
                            isVisible
                              ? `Hide ${category.label}`
                              : `Show ${category.label}`
                          }
                        >
                          {isVisible ? (
                            <Eye className="h-3.5 w-3.5 shrink-0 text-coval-text-muted" />
                          ) : (
                            <EyeOff className="h-3.5 w-3.5 shrink-0 text-coval-text-muted/40" />
                          )}
                          <span className="text-sm truncate">
                            {category.label}
                          </span>
                          <span className="ml-auto text-xs tabular-nums text-coval-text-muted/60">
                            {category.count}
                          </span>
                        </button>
                      </Tooltip>
                    )
                  })
                )}
              </AccordionContent>
            </AccordionItem>
          )}

          {showColors && (
            <AccordionItem value="colors">
              <AccordionTrigger className="px-3">Colors</AccordionTrigger>
              <AccordionContent className="px-3 pb-3">
                <CompareByColorSection
                  distinctCompareByValues={distinctCompareByValues}
                  compareByValueToColor={compareByValueToColor}
                  setCompareByColor={setCompareByColor}
                />
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </PopoverContent>
    </Popover>
  )
}
